import FormFieldData from './FormFieldData'
import { dateTimeFormat } from '../../utility/utility_functions'

export default class FormField {
  static ERROR_MESSAGES = {
    MAX_VALUE: 'This field must have a value less than or equal to %s.',
    MIN_VALUE: 'This field must have a value greater than or equal to %s.',
    INPUT_REQUIRED: 'This field is required',
    VALIDATION_PATTERN: 'This field does not match the requested format.'
  }

  constructor (id, index, uid, type, label, layout, properties, dataGroup) {
    this.id = id
    this.fieldSetIndex = index
    this.uid = uid
    this.type = type
    this.label = label
    this.layout = layout
    this.properties = properties
    this.dataGroup = dataGroup
  }

  get dataFieldId () {
    return this.dataGroup + '@' + this.uid + '#' + this.fieldSetIndex
  }

  updateId (formData, id) {
    this.id = id
    // update the field data
    let fieldData = formData.getFieldForUid(this.uid)
    let temp = fieldData.id
    fieldData.id = id
    formData.fields[id] = fieldData
    delete formData.fields[temp]
  }

  getFieldData (formData) {
    return formData ? formData.fields[this.dataFieldId] : {}
  }

  getFieldValue (formData) {
    return this.getFieldData(formData).value
  }

  setFieldValue (formData, value) {
    this.getFieldData(formData).value = value
  }

  static asFormFields (json, fieldSetIndex) {
    return json.map(formField => {
      return FormField.asFormField(formField, fieldSetIndex)
    })
  }

  static createFieldId (field, index) {
    // Generates a unique id of data group and uid
    return field.data_group + '@' + field.uid + '#' + index
  }

  static asFormField (json, fieldSetIndex) {
    return new FormField(
      FormField.createFieldId(json, fieldSetIndex),
      fieldSetIndex,
      json.uid,
      json.input_type,
      json.label,
      json.layout ? json.layout : { position: -1, layout_type: 'vertical' },
      json.properties ? json.properties : {},
      json.data_group
    )
  }

  createFormFieldData (subGroup) {
    return FormFieldData.asFormFieldData({
      id: this.id,
      uid: this.uid,
      input_type: this.type,
      value: FormField.parseValue(this.properties.default_value, this.type, this.properties),
      properties: this.properties,
      data_group: this.dataGroup,
      data_sub_group: subGroup
    })
  }

  static parseValue (value, type, properties) {
    if (!value) {
      if (FormField.isCheckboxField(type)) {
        return false
      }
      if (FormField.isTextAreaField(type)) {
        return value
      }
      if (FormField.isMultipleCheckboxField(type)) {
        return []
      }
      if (FormField.isTagInputField(type)) {
        return []
      }
      if (properties.default_value) {
        return properties.default_value
      }
        return null
    }

    if (FormField.isDateField(type)) {
      return dateTimeFormat(value)
    }
    if (FormField.isNumberField(type)) {
      return parseInt(value)
    }
    if (FormField.isCheckboxField(type)) {
      return value === true || value === 1 || value.toLowerCase() === 'true' || value.toLowerCase() === '1'
    }
    if (FormField.isMultipleCheckboxField(type)) {
      return typeof value === 'object' ? value : JSON.parse(value)
    }
    if (FormField.isTagInputField(type)) {
      return typeof value === 'object' ? value : JSON.parse(value.replace(/'/g, '"'))
    }
    return value
  }

  static hasValue (value, type) {
    if (FormField.isNumberField(type)) {
      if (value === 0) {
        return true
      }
    }
    // more custom value checking if needed
    return !!value
  }

  static validateValue (value, type, validation) {
    if (!validation.properties || Object.keys(validation.properties).length === 0) {
      validation.is_valid = true
      validation.message = ''
      return true
    }
    if (this.hasValue(value, type)) {
      if (validation.properties.hasOwnProperty('validation_pattern') && validation.properties.validation_pattern) {
        if (FormField.isTextField(type) || FormField.isTextAreaField(type) || FormField.isNumberField(type) || FormField.isPasswordField(type) || FormField.isEmailField(type) || FormField.isDateField(type) || FormField.isPostalCodeField(type) || FormField.isExamChoiceField(type)) {
          let regString = validation.properties.validation_pattern
          if (regString[0] !== '^') regString = '^' + regString
          regString = regString + '$'

          let reg = new RegExp(regString)
          if (!reg.test(value)) {
            validation.is_valid = false
            validation.message = FormField.ERROR_MESSAGES.VALIDATION_PATTERN
            return false
          }
        }
      }

      if (validation.properties.hasOwnProperty('max_value') && validation.properties.max_value) {
        if (FormField.isNumberField(type)) {
          let number = parseInt(value)
          let maxNumber = parseInt(validation.properties.max_value)
          if (number && number > maxNumber) {
            validation.is_valid = false
            validation.message = FormField.ERROR_MESSAGES.MAX_VALUE.replace(/%s/, maxNumber.toString())
            return false
          }
        } else if (FormField.isDateField(type)) {
          let date = new Date(value)
          let maxDate = new Date(validation.properties.max_value)
          if (date > maxDate) {
            validation.is_valid = false
            validation.message = FormField.ERROR_MESSAGES.MAX_VALUE.replace(/%s/, maxDate.toLocaleDateString())
            return false
          }
        }
      }

      if (validation.properties.hasOwnProperty('min_value') && validation.properties.min_value) {
        if (FormField.isNumberField(type)) {
          let number = parseInt(value)
          let minNumber = parseInt(validation.properties.min_value)
          if (number && number < minNumber) {
            validation.is_valid = false
            validation.message = FormField.ERROR_MESSAGES.MIN_VALUE.replace(/%s/, minNumber.toString())
            return false
          }
        } else if (FormField.isDateField(type)) {
          let date = new Date(value)
          let minDate = new Date(validation.properties.min_value)
          if (date < minDate) {
            validation.is_valid = false
            validation.message = FormField.ERROR_MESSAGES.MIN_VALUE.replace(/%s/, minDate.toLocaleDateString())
            return false
          }
        }
      }
    } else {
      if (validation.properties.hasOwnProperty('input_required') && validation.properties.input_required) {
        validation.is_valid = false
        validation.message = FormField.ERROR_MESSAGES.INPUT_REQUIRED
        return false
      }
    }
    if (FormField.isMultipleFileField(type)) {
      if (validation.properties.hasOwnProperty('input_required') && validation.properties.input_required) {
        if (Array.isArray(value) && value.length === 0) {
          validation.is_valid = false
          validation.message = FormField.ERROR_MESSAGES.INPUT_REQUIRED
          return false
        }
      }
    }
    validation.is_valid = true
    validation.message = ''
    return true
  }

  static isTextAreaField (type) {
    return type.toLowerCase() === 'textarea'
  }

  static isTextField (type) {
    return type.toLowerCase() === 'text'
  }

  static isPostalCodeField (type) {
    return type.toLowerCase() === 'postal-code'
  }

  static isExamChoiceField (type) {
    return type.toLowerCase() === 'exam-choice'
  }

  static isEmailField (type) {
    return type.toLowerCase() === 'email'
  }

  static isPasswordField (type) {
    return type.toLowerCase() === 'password'
  }

  static isNumberField (type) {
    return type.toLowerCase() === 'number'
  }

  static isDateField (type) {
    return type.toLowerCase() === 'date'
  }

  static isFileField (type) {
    return type.toLowerCase() === 'file' || type.toLowerCase() === 'multiple-files'
  }
  static isMultipleFileField (type) {
    return type.toLowerCase() === 'multiple-files'
  }

  static isMultipleCheckboxField (type) {
    return type.toLowerCase() === 'multiple-checkbox'
  }

  static isTagInputField (type) {
    return type.toLowerCase() === 'tag-input'
  }

  static isCheckboxField (type) {
    return type.toLowerCase() === 'checkbox'
  }

  static isRadioField (type) {
    return type.toLowerCase() === 'radio'
  }

  static isSelectField (type) {
    return type.toLowerCase() === 'select'
  }

  static isRangeField (type) {
    return type.toLowerCase() === 'range'
  }
}
